import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { Button } from "./ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/reviews", label: "Reviews" },
  { to: "/blog", label: "Blog" },
  { to: "/guides", label: "Guides" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();

  const dashHref = user?.role === "admin" ? "/admin" : user?.role === "designer" ? "/designer" : "/dashboard";

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#1a3c5e]/10 shadow-[0_1px_0_rgba(26,60,94,0.06)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo">
          <img
            src={settings.logo_image}
            alt="HouseDesignWala"
            className="w-16 h-16 rounded-full object-cover"
          />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-[#1a3c5e] text-lg tracking-tight">HouseDesignWala<span className="text-[#F7941D]">™</span></div>
            <div className="hidden sm:block text-[10px] text-gray-500 font-medium italic">{settings.tagline}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive ? "text-[#F7941D] bg-[#FEF3E2]" : "text-[#1a3c5e]/80 hover:text-[#1a3c5e] hover:bg-[#FEF3E2]/60"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                data-testid="navbar-user-menu"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#1a3c5e]/15 hover:bg-[#FEF3E2]/60 transition"
              >
                <div className="w-7 h-7 rounded-full bg-[#1a3c5e] text-white text-xs flex items-center justify-center font-semibold">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </div>
                <span className="text-sm font-medium text-[#1a3c5e] max-w-[120px] truncate">{user.name}</span>
                <ChevronDown className="w-4 h-4 text-[#1a3c5e]/60" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#1a3c5e]/10 overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#1a3c5e]/10">
                    <div className="text-xs text-[#1a3c5e]/60">Signed in as</div>
                    <div className="text-sm font-semibold text-[#1a3c5e] truncate">{user.email}</div>
                    <div className="text-[10px] mt-1 inline-block px-2 py-0.5 rounded bg-[#FEF3E2] text-[#F7941D] font-bold uppercase tracking-wide">{user.role}</div>
                  </div>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate(dashHref); }}
                    data-testid="navbar-dashboard-btn"
                    className="w-full px-4 py-2.5 text-sm text-[#1a3c5e] hover:bg-[#FEF3E2] flex items-center gap-2"
                  >
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </button>
                  <button
                    onClick={async () => { await logout(); setShowUserMenu(false); navigate("/"); }}
                    data-testid="navbar-logout-btn"
                    className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-[#1a3c5e]/10"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" data-testid="navbar-login-btn">
                <Button variant="outline" className="border-[#1a3c5e] text-[#1a3c5e] hover:bg-[#1a3c5e] hover:text-white rounded-xl">Login</Button>
              </Link>
              <Link to="/services" data-testid="navbar-order-btn">
                <Button className="bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-xl shadow-md">Order Now</Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-lg text-[#1a3c5e] hover:bg-[#FEF3E2]"
          onClick={() => setOpen((o) => !o)}
          data-testid="navbar-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#1a3c5e]/10">
          <div className="px-4 py-4 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive ? "bg-[#FEF3E2] text-[#F7941D]" : "text-[#1a3c5e] hover:bg-[#FEF3E2]/60"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-[#1a3c5e]/10 flex gap-2">
              {user ? (
                <>
                  <Link to={dashHref} className="flex-1" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-[#1a3c5e] hover:bg-[#122a42] text-white rounded-xl">Dashboard</Button>
                  </Link>
                  <Button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} variant="outline" className="rounded-xl">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" className="flex-1" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full border-[#1a3c5e] text-[#1a3c5e] rounded-xl">Login</Button>
                  </Link>
                  <Link to="/services" className="flex-1" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-xl">Order Now</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
